import { useState } from 'react';
import {
  CARD_SUITS,
  DEFAULT_BETTING,
  ROULETTE_LABELS,
  ROULETTE_PAYS,
  cardName,
  formatPence,
  type BetView,
  type BettingReply,
  type BreakLength,
  type FixtureView,
  type FootballPick,
  type PlaceBetRequest,
  type RouletteBet,
} from '@london-life/contracts';
import { api } from '../lib/api';
import { useCatalogue } from '../lib/content';
import { actionKey, londonClock, londonDay, londonWhen, typedPence } from '../lib/format';
import { PhoneAppFrame, usePhoneApp, type PhoneAppProps } from '../lib/phone-app';

type Tab = 'football' | 'highlow' | 'roulette' | 'mine' | 'safer';
const TABS: { id: Tab; label: string }[] = [
  { id: 'football', label: '⚽ Football' },
  { id: 'highlow', label: '🃏 High-low' },
  { id: 'roulette', label: '🎡 Roulette' },
  { id: 'mine', label: '🧾 My bets' },
  { id: 'safer', label: '🛡️ Safer play' },
];
const BREAK_LABELS: Record<Exclude<BreakLength, 'forever'>, string> = {
  day: '24 hours',
  week: 'A week',
  month: 'A month',
  six_weeks: 'Six weeks',
};
const pickLabel = (fixture: FixtureView, pick: FootballPick) =>
  pick === 'draw' ? 'Draw' : `${pick === 'home' ? fixture.home : fixture.away} to win`;
const resultLabel = (fixture: FixtureView) =>
  fixture.result === 'draw' ? 'Draw' : `${fixture.result === 'home' ? fixture.home : fixture.away} won`;

// Bets, as Lagos Life's ⚽ LagosBet, to the rules (contracts betting.ts): game money only, the notice on every
// page, odds before every bet, the day's limit and the player's own, breaks, a switch-off and a reality check
export function BetsScreen(props: PhoneAppProps) {
  const catalogue = useCatalogue();
  const betting = catalogue?.betting ?? DEFAULT_BETTING;
  const { reply, failure, busy, load, run } = usePhoneApp(() => api.bets.mine(), props);
  const [tab, setTab] = useState<Tab>('football');
  const place = (body: PlaceBetRequest) => run(() => api.bets.place(body, actionKey('bet')));
  const control = (body: Parameters<typeof api.bets.control>[0]) => run(() => api.bets.control(body));
  const stopped = reply && (reply.off || reply.breakUntil);
  return (
    <PhoneAppFrame
      id="bets"
      title={`⚽ ${betting.name}`}
      className="bets-screen"
      closeLabel={`Close ${betting.name}`}
      onClose={props.onClose}
      failure={failure}
      onRetry={() => void load()}
      loading={reply ? undefined : 'Opening the book…'}
    >
      <p className="bets-notice" role="note">
        {betting.notice}
      </p>
      {reply && (
        <div className="business-detail">
          <section className="bets-money" aria-label="Your betting today">
            <p>
              <small>Wallet</small>
              <strong>{formatPence(reply.walletPence)}</strong>
            </p>
            <p>
              <small>Left to bet today</small>
              <strong>{formatPence(reply.leftTodayPence)}</strong>
            </p>
          </section>
          <div className="flights-tabs bets-tabs" role="tablist" aria-label={betting.name}>
            {TABS.map((item) => (
              <button
                type="button"
                key={item.id}
                role="tab"
                aria-selected={tab === item.id}
                className={tab === item.id ? 'active' : ''}
                onClick={() => setTab(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          {stopped && ['football', 'highlow', 'roulette'].includes(tab) && (
            <p className="business-warning" role="status">
              {reply.off
                ? 'Betting is switched off on your account for good.'
                : `You are on a break from betting until ${londonDay(reply.breakUntil!)}.`}
            </p>
          )}
          {!stopped && tab === 'football' && <Football reply={reply} busy={busy} onBet={place} />}
          {!stopped && tab === 'highlow' && (
            <HighLow reply={reply} busy={busy} onBet={place} onNewCard={() => void control({ action: 'card' })} />
          )}
          {!stopped && tab === 'roulette' && <Roulette reply={reply} busy={busy} onBet={place} />}
          {tab === 'mine' && <MyBets bets={reply.bets} />}
          {tab === 'safer' && <SaferPlay reply={reply} busy={busy} help={betting.help} onControl={control} />}
          {reply.realityCheck && (
            <div className="bets-reality" role="alertdialog" aria-modal="true" aria-labelledby="bets-reality-title">
              <div>
                <strong id="bets-reality-title">⏱️ Reality check</strong>
                <p>
                  You have placed {reply.sessionBets} bets this session and staked {formatPence(reply.stakedTodayPence)}{' '}
                  today. Take a break whenever you like.
                </p>
                <div className="business-chips">
                  <button type="button" disabled={busy} onClick={() => void control({ action: 'seen' })}>
                    Carry on
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void control({ action: 'seen' }).then(() => setTab('safer'))}
                  >
                    Take a break
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </PhoneAppFrame>
  );
}

type BetProps = { reply: BettingReply; busy: boolean; onBet: (body: PlaceBetRequest) => Promise<boolean> };

// the stake: quick amounts from the content's smallest bet, or one typed in; never more than is left today
function Stake({ reply, value, onChange }: { reply: BettingReply; value: number; onChange: (pence: number) => void }) {
  const catalogue = useCatalogue();
  const betting = catalogue?.betting ?? DEFAULT_BETTING;
  const top = Math.min(betting.maxStakePence, reply.leftTodayPence);
  const quick = [1, 5, 10, 50, 100].map((times) => betting.minStakePence * times).filter((pence) => pence <= top);
  return (
    <div className="bets-stake">
      <div className="business-chips" role="group" aria-label="Stake">
        {quick.map((pence) => (
          <button
            type="button"
            key={pence}
            className={value === pence ? 'active' : ''}
            aria-pressed={value === pence}
            onClick={() => onChange(pence)}
          >
            {formatPence(pence)}
          </button>
        ))}
      </div>
      <label>
        <small>
          Stake ({formatPence(betting.minStakePence)} to {formatPence(top)})
        </small>
        <input
          inputMode="decimal"
          value={value ? String(value / 100) : ''}
          onChange={(event) => onChange(typedPence(event.target.value))}
        />
      </label>
    </div>
  );
}
const stakeOk = (reply: BettingReply, min: number, max: number, stake: number) =>
  stake >= min && stake <= Math.min(max, reply.leftTodayPence) && stake <= reply.walletPence;
function useStake(reply: BettingReply) {
  const betting = useCatalogue()?.betting ?? DEFAULT_BETTING;
  const [stake, setStake] = useState(betting.minStakePence);
  return { stake, setStake, ok: stakeOk(reply, betting.minStakePence, betting.maxStakePence, stake) };
}
// what a winning bet returns at these odds (stake included)
const returns = (stake: number, odds: number) => formatPence(Math.floor(stake * odds));

function Football({ reply, busy, onBet }: BetProps) {
  const [slip, setSlip] = useState<{ fixture: FixtureView; pick: FootballPick } | null>(null);
  const { stake, setStake, ok } = useStake(reply);
  const closed = !reply.footballOpen;
  return (
    <>
      <p className="business-muted">
        Today&apos;s games. Results at {londonClock(reply.resultsAt)}
        {closed ? ': betting on today’s games has closed.' : '; bets close then.'}
      </p>
      <ul className="bets-fixtures" aria-label="Today's games">
        {reply.fixtures.map((fixture) => (
          <li key={fixture.id}>
            <strong>
              {fixture.home} <small>v</small> {fixture.away}
            </strong>
            {fixture.result ? (
              <em>{resultLabel(fixture)}</em>
            ) : (
              <div className="bets-odds">
                {(['home', 'draw', 'away'] as const).map((pick) => (
                  <button
                    type="button"
                    key={pick}
                    disabled={closed}
                    aria-pressed={slip?.fixture.id === fixture.id && slip.pick === pick}
                    aria-label={`${pickLabel(fixture, pick)} at ${fixture.odds[pick].toFixed(2)}`}
                    onClick={() => setSlip({ fixture, pick })}
                  >
                    <small>{pick === 'home' ? fixture.home : pick === 'away' ? fixture.away : 'Draw'}</small>
                    <b>{fixture.odds[pick].toFixed(2)}</b>
                  </button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
      {slip && !closed && (
        <section className="bets-slip" aria-label="Bet slip">
          <strong>
            {pickLabel(slip.fixture, slip.pick)} · {slip.fixture.odds[slip.pick].toFixed(2)}
          </strong>
          <Stake reply={reply} value={stake} onChange={setStake} />
          <button
            type="button"
            className="city-card-go"
            disabled={busy || !ok}
            onClick={() =>
              void onBet({ game: 'football', fixture: slip.fixture.id, pick: slip.pick, stakePence: stake }).then(
                (done) => done && setSlip(null),
              )
            }
          >
            Bet {formatPence(stake)} · returns {returns(stake, slip.fixture.odds[slip.pick])}
          </button>
        </section>
      )}
    </>
  );
}

function HighLow({ reply, busy, onBet, onNewCard }: BetProps & { onNewCard: () => void }) {
  const { stake, setStake, ok } = useStake(reply);
  const name = cardName(reply.card);
  const red = name.endsWith(CARD_SUITS[1]) || name.endsWith(CARD_SUITS[2]);
  return (
    <>
      <p className="business-muted">Is the next card higher or lower? Ace is high; the same rank loses.</p>
      <div className={`bets-card${red ? ' red' : ''}`} aria-label={`The card: ${name}`}>
        {name}
      </div>
      <Stake reply={reply} value={stake} onChange={setStake} />
      <div className="bets-odds">
        {(['higher', 'lower'] as const).map((pick) => {
          const odds = reply.odds[pick];
          return (
            <button
              type="button"
              key={pick}
              disabled={busy || !ok || odds === null}
              onClick={() => void onBet({ game: 'highlow', pick, stakePence: stake })}
            >
              <small>{pick === 'higher' ? '⬆️ Higher' : '⬇️ Lower'}</small>
              <b>{odds === null ? '—' : odds.toFixed(2)}</b>
            </button>
          );
        })}
      </div>
      <button type="button" className="flights-terminal" disabled={busy} onClick={onNewCard}>
        🔄 New card (free)
      </button>
    </>
  );
}

function Roulette({ reply, busy, onBet }: BetProps) {
  const { stake, setStake, ok } = useStake(reply);
  const [bet, setBet] = useState<RouletteBet>('red');
  const [number, setNumber] = useState(17);
  return (
    <>
      <p className="business-muted">One zero. Zero loses every bet but a bet on zero itself.</p>
      <div className="business-chips bets-roulette" role="group" aria-label="Roulette bet">
        {(Object.keys(ROULETTE_LABELS) as RouletteBet[]).map((item) => (
          <button
            type="button"
            key={item}
            className={bet === item ? 'active' : ''}
            aria-pressed={bet === item}
            onClick={() => setBet(item)}
          >
            {ROULETTE_LABELS[item]} <small>pays {ROULETTE_PAYS[item]}×</small>
          </button>
        ))}
      </div>
      {bet === 'number' && (
        <label className="bets-number">
          <small>Your number (0 to 36)</small>
          <input
            type="number"
            min={0}
            max={36}
            value={number}
            onChange={(event) => setNumber(Math.max(0, Math.min(36, Math.trunc(Number(event.target.value) || 0))))}
          />
        </label>
      )}
      <Stake reply={reply} value={stake} onChange={setStake} />
      <button
        type="button"
        className="city-card-go"
        disabled={busy || !ok}
        onClick={() =>
          void onBet({ game: 'roulette', bet, ...(bet === 'number' ? { number } : {}), stakePence: stake })
        }
      >
        🎡 Spin · {formatPence(stake)} on {bet === 'number' ? number : ROULETTE_LABELS[bet]}
      </button>
    </>
  );
}

function MyBets({ bets }: { bets: BetView[] }) {
  if (!bets.length) return <p className="business-muted">No bets yet.</p>;
  return (
    <ul className="bets-mine" aria-label="Your bets">
      {bets.map((bet) => (
        <li key={bet.id} className={bet.status}>
          <span>
            <strong>{bet.label}</strong>
            <small>
              {formatPence(bet.stakePence)} at {bet.odds.toFixed(2)} · {londonWhen(bet.placedAt)}
              {bet.result ? ` · ${bet.result}` : ''}
            </small>
          </span>
          <b>
            {bet.status === 'open'
              ? `Results ${bet.settlesAt ? londonClock(bet.settlesAt) : ''}`
              : bet.status === 'won'
                ? `Won ${formatPence(bet.paidPence)}`
                : 'Lost'}
          </b>
        </li>
      ))}
    </ul>
  );
}

function SaferPlay({
  reply,
  busy,
  help,
  onControl,
}: {
  reply: BettingReply;
  busy: boolean;
  help: string;
  onControl: (body: Parameters<typeof api.bets.control>[0]) => Promise<boolean>;
}) {
  const [limit, setLimit] = useState(reply.ownLimitPence ? String(reply.ownLimitPence / 100) : '');
  const [switching, setSwitching] = useState(false);
  return (
    <>
      <section className="business-sums" aria-label="Your limit">
        <p>
          <span>Staked today</span>
          <span>{formatPence(reply.stakedTodayPence)}</span>
        </p>
        <p>
          <span>Your own daily limit</span>
          <span>{reply.ownLimitPence === null ? 'None' : formatPence(reply.ownLimitPence)}</span>
        </p>
        <small>A lower limit starts now. A higher one, or none, starts in 24 hours.</small>
      </section>
      <label className="bets-number">
        <small>Daily limit (£)</small>
        <input inputMode="decimal" value={limit} onChange={(event) => setLimit(event.target.value)} />
      </label>
      <div className="business-chips">
        <button
          type="button"
          disabled={busy || !typedPence(limit)}
          onClick={() => void onControl({ action: 'limit', dailyPence: typedPence(limit) })}
        >
          Set my limit
        </button>
        {reply.ownLimitPence !== null && (
          <button type="button" disabled={busy} onClick={() => void onControl({ action: 'limit', dailyPence: null })}>
            Remove it
          </button>
        )}
      </div>
      <strong className="business-step-title">TAKE A BREAK</strong>
      {reply.off ? (
        <p className="business-muted">Betting is switched off on your account for good.</p>
      ) : (
        <>
          <p className="business-muted">
            {reply.breakUntil
              ? `On a break until ${londonDay(reply.breakUntil)}. It can be made longer, not shorter.`
              : 'No bets at all until the break is over. It can’t be cut short.'}
          </p>
          <div className="business-chips" role="group" aria-label="Break">
            {(Object.keys(BREAK_LABELS) as (keyof typeof BREAK_LABELS)[]).map((length) => (
              <button
                type="button"
                key={length}
                disabled={busy}
                onClick={() => void onControl({ action: 'break', length })}
              >
                {BREAK_LABELS[length]}
              </button>
            ))}
          </div>
          {switching ? (
            <div className="jet-sell">
              <span>Switch betting off on your account for good? This can’t be undone.</span>
              <button type="button" onClick={() => setSwitching(false)} disabled={busy}>
                Keep it on
              </button>
              <button
                type="button"
                className="jet-sell-yes"
                disabled={busy}
                onClick={() => void onControl({ action: 'break', length: 'forever' }).then(() => setSwitching(false))}
              >
                Switch off
              </button>
            </div>
          ) : (
            <button type="button" className="flights-terminal" onClick={() => setSwitching(true)}>
              Switch betting off for good…
            </button>
          )}
        </>
      )}
      <p className="bets-help">{help}</p>
    </>
  );
}
